import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBrokers } from '../lib/api';
import { SEO, buildComparisonSchema } from '../components/SEO';
import { ExternalLink, Check, X } from 'lucide-react';

export default function ComparePage() {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBrokers().then(res => { setBrokers(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  }

  const compareFields = [
    { label: 'Founded', render: (b) => b.founded },
    { label: 'Regulation', render: (b) => b.regulation },
    { label: 'Headquarters', render: (b) => b.headquarters },
    { label: 'Min. Deposit', render: (b) => <span className="font-semibold">{b.min_deposit}</span> },
    { label: 'Platforms', render: (b) => b.platforms },
    { label: 'Instruments', render: (b) => b.instruments },
    { label: 'Account Types', render: (b) => b.account_types },
    { label: 'Max Leverage', render: (b) => b.leverage },
    { label: 'Spreads', render: (b) => b.fees?.spreads },
    { label: 'Commission', render: (b) => b.fees?.commission },
    { label: 'Swap Fees', render: (b) => b.fees?.swap_fees },
    { label: 'Inactivity Fee', render: (b) => b.fees?.inactivity_fee },
    { label: 'Deposit Methods', render: (b) => b.deposit_methods },
    { label: 'Customer Support', render: (b) => b.customer_support },
  ];

  return (
    <div data-testid="compare-page">
      <SEO
        title="Compare Forex & CFD Brokers"
        description="Side-by-side comparison of forex and CFD brokers across key features, fees, regulation, and trading conditions."
        canonical="/compare"
        jsonLd={buildComparisonSchema(brokers)}
      />
      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-['Outfit'] mb-4">
            Compare Forex & CFD Brokers
          </h1>
          <p className="text-base text-slate-300 max-w-2xl">
            Side-by-side comparison of all reviewed brokers across key features, fees, regulation, and trading conditions.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full overflow-auto rounded-lg border border-slate-200" data-testid="full-comparison-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="sticky left-0 z-10 bg-slate-50 px-5 py-4 text-left text-xs font-semibold text-slate-900 uppercase tracking-wider min-w-[160px]">
                  Feature
                </th>
                {brokers.map(b => (
                  <th key={b.slug} className="px-4 py-4 text-center min-w-[170px]">
                    <span className="text-sm font-bold text-slate-900 font-['Outfit'] uppercase">{b.name}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {compareFields.map((field, i) => (
                <tr key={field.label} className="border-b border-slate-200 last:border-0 hover:bg-slate-50/50 transition-colors">
                  <td className="sticky left-0 z-10 bg-white px-5 py-3.5 text-sm font-medium text-slate-900 border-r border-slate-100">
                    {field.label}
                  </td>
                  {brokers.map(b => (
                    <td key={b.slug} className="px-4 py-3.5 text-sm text-slate-700 text-center">{field.render(b)}</td>
                  ))}
                </tr>
              ))}
              {/* Pros row */}
              <tr className="border-b border-slate-200">
                <td className="sticky left-0 z-10 bg-white px-5 py-3.5 text-sm font-medium text-green-700 border-r border-slate-100">
                  Key Advantages
                </td>
                {brokers.map(b => (
                  <td key={b.slug} className="px-4 py-3.5 text-left align-top">
                    <ul className="space-y-1.5">
                      {b.pros.slice(0, 3).map((p, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-xs text-slate-600">
                          <Check className="w-3 h-3 text-green-600 shrink-0 mt-0.5" /> {p}
                        </li>
                      ))}
                    </ul>
                  </td>
                ))}
              </tr>
              {/* Cons row */}
              <tr className="border-b border-slate-200">
                <td className="sticky left-0 z-10 bg-white px-5 py-3.5 text-sm font-medium text-red-700 border-r border-slate-100">
                  Key Disadvantages
                </td>
                {brokers.map(b => (
                  <td key={b.slug} className="px-4 py-3.5 text-left align-top">
                    <ul className="space-y-1.5">
                      {b.cons.slice(0, 2).map((c, j) => (
                        <li key={j} className="flex items-start gap-1.5 text-xs text-slate-600">
                          <X className="w-3 h-3 text-red-500 shrink-0 mt-0.5" /> {c}
                        </li>
                      ))}
                    </ul>
                  </td>
                ))}
              </tr>
              {/* Action row */}
              <tr>
                <td className="sticky left-0 z-10 bg-white px-5 py-4 text-sm font-medium text-slate-900 border-r border-slate-100">
                  Actions
                </td>
                {brokers.map(b => (
                  <td key={b.slug} className="px-4 py-4 text-center">
                    <div className="flex flex-col gap-2 items-center">
                      <Link
                        to={`/${b.slug}`}
                        className="inline-flex items-center justify-center rounded-md text-xs font-medium bg-slate-900 text-white hover:bg-slate-800 h-8 px-4 transition-colors w-full max-w-[140px]"
                        data-testid={`compare-review-${b.slug}`}
                      >
                        Read Review
                      </Link>
                      <a
                        href={b.affiliate_url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center justify-center gap-1 rounded-md text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 h-8 px-4 transition-colors w-full max-w-[140px]"
                        data-testid={`compare-visit-${b.slug}`}
                      >
                        Visit <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-600">Note:</strong> This comparison is based on information available at the time of review. Trading conditions, fees, and regulations may change. Always verify current information directly with the broker. Broker links may be affiliate links. This comparison does not constitute a recommendation.
          </p>
        </div>
      </div>
    </div>
  );
}
